<template>
  <s-modal
    v-model="showModal"
    cancel-confirm-message-code="MC006"
    ok-confirm-message-code="MC049"
    :confirm-type="confirmationTypes.YES_NO"
    :title="$t('diagnostics-configurations.configuration-modal-title')"
    :ok-title="$t('general.save')"
    :ok-disabled="isSaveDisabled"
    :cancel-disabled="loading"
    :ok-promise="editDiagnostics"
    :is-loading="loading"
    @cancel="cancel"
  >
    <b-container>
      <b-row>
        <label class="network-report-filters__field-label">
          {{ $t('diagnostics-configurations.classification') }}
        </label>
        <s-select
          v-model="selectedClassification"
          text-field="name"
          value-field="id"
          :options="classificationOptions"
          class="diagnostic-configuration-modal__classification-select"
          @change="classificationValueHandler"
        />
      </b-row>
      <b-row class="my-3">
        <b-checkbox
          v-model="generatesAlarmHandler"
          :disabled="shouldDisableGeneratesAlarmCheckbox"
        >
          {{ $t('diagnostics-configurations.generate-alarm-title') }}
        </b-checkbox>
      </b-row>
      <b-row v-if="shouldShowAlert">
        <b-alert
          class="diagnostic-configuration-modal__alert text-center"
          variant="warning"
          show
        >
          <strong>{{$t('general.attention')}}!</strong>
          {{ message }}
        </b-alert>
      </b-row>
    </b-container>
  </s-modal>
</template>

<script>
import { ClassificationType } from 'sigma-mappings';
import LoadingMixin from '@/services/loadingMixin';
import DiagnosticService from '@/domain/services/generalPanel/adminPanel/diagnosticsConfigurations/diagnosticsConfigurations';
import { isSameArray } from '@/services/arrayUtils';
import ConfirmationTypes from '@/constants/confirmationTypes';

export default {
  name: 'DiagnosticConfigurationModal',
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    payload: {
      type: Object,
      default: () => ({}),
      required: true,
    },
    generatesAlarm: {
      type: Boolean,
      default: false,
    },
    diagnosticClassification: {
      type: String,
      default: null,
    },
    selectedDiagnostics: {
      type: Array,
      required: true,
      default: () => ([]),
    },
  },
  mixins: [LoadingMixin],
  data: () => ({
    initialSelectedClassification: null,
    initialGeneratesAlarm: null,
  }),
  computed: {
    confirmationTypes() {
      return ConfirmationTypes;
    },
    isSaveDisabled() {
      return !this.selectedClassification || this.loading;
    },
    shouldShowAlert() {
      return this.selectedDiagnostics.length > 1
        || (!this.sameClassificationSetup || !this.sameAlarmSetup);
    },
    sameClassificationSetup() {
      const { selectedDiagnostics } = this;
      const prop = 'classifications';

      return isSameArray(selectedDiagnostics, prop);
    },
    sameAlarmSetup() {
      const { selectedDiagnostics } = this;
      const prop = 'alarms';

      return isSameArray(selectedDiagnostics, prop);
    },
    message() {
      const diagnosticsNumber = (this.selectedDiagnostics || []).length;
      const assetsNumber = this.payload.amountAssetsAffected;
      const MA073 = this.$t('messages.MA073.message',
        { DiagnosticsNumber: diagnosticsNumber, AssetsNumber: assetsNumber });
      const MA074 = this.$t('messages.MA074.message',
        { DiagnosticsNumber: diagnosticsNumber, AssetsNumber: assetsNumber });
      const MA081 = this.$t('messages.MA081.message',
        { DiagnosticsNumber: diagnosticsNumber, AssetsNumber: assetsNumber });
      const MA075 = this.$t('messages.MA075.message',
        { DiagnosticsNumber: diagnosticsNumber, AssetsNumber: assetsNumber });

      if (!this.sameClassificationSetup && !this.sameAlarmSetup) {
        return `${MA081}`;
      }

      if (!this.sameClassificationSetup) {
        return `${MA073}`;
      }

      if (!this.sameAlarmSetup) {
        return `${MA074}`;
      }

      return `${MA075}.`;
    },
    shouldDisableGeneratesAlarmCheckbox() {
      return this.userLacksPER029 || this.classificationIsObservation;
    },
    userLacksPER029() {
      return !this.$hasUserPermission('PER029');
    },
    classificationIsObservation() {
      return this.diagnosticClassification === ClassificationType.OBSERVATION;
    },
    classificationOptions() {
      return [
        {
          name: this.$t('enums.commons.classification-type.observation'),
          id: ClassificationType.OBSERVATION,
        },
        {
          name: this.$t('enums.commons.classification-type.ordinary'),
          id: ClassificationType.ORDINARY,
        },
        {
          name: this.$t('enums.commons.classification-type.urgent'),
          id: ClassificationType.URGENT,
        },
        {
          name: this.$t('enums.commons.classification-type.critical'),
          id: ClassificationType.CRITICAL,
        },
      ];
    },
    selectedClassification: {
      get() {
        return this.diagnosticClassification;
      },
      set(value) {
        this.$emit('update:diagnostic-classification', value);
      },
    },
    generatesAlarmHandler: {
      get() {
        return this.generatesAlarm;
      },
      set(value) {
        this.$emit('update:generates-alarm', value);
      },
    },
    showModal: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit('input', value);
      },
    },
  },
  methods: {
    classificationValueHandler(input) {
      if (input === ClassificationType.OBSERVATION) {
        this.$emit('update:generates-alarm', false);
      }
    },
    cancel() {
      this.$emit('update:diagnostic-classification', null);
      this.$emit('input', false);
    },
    editDiagnostics() {
      const { payload } = this;
      this.startLoading();

      const requests = [];

      if ((payload.chromaDiagnosticsIds || []).length) {
        requests.push(DiagnosticService.putConfigurations({
          ...payload,
          diagnosticsIds: payload.chromaDiagnosticsIds,
        }));
      }

      if ((payload.oscillographyDiagnosticsIds || []).length) {
        requests.push(DiagnosticService.putOscillographyAnalysisConfigurations({
          ...payload,
          diagnosticsIds: payload.oscillographyDiagnosticsIds,
        }));
      }

      return Promise.all(requests)
        .then(() => {
          this.$pushAlert({ code: 'MS006' });
          this.$emit('update-classifications');
          this.$emit('input', false);
        })
        .finally(() => {
          this.endLoading();
        });
    },
  },
};
</script>

<style lang="sass" scoped>
 .diagnostic-configuration-modal
    &__alert
      width: 100%
    &__classification-select
      ::v-deep .dropdown-menu
        max-height: 130px
        overflow-y: scroll
        padding-top: 0
</style>
